import React, { useState, useEffect, useRef } from 'react';
import {
    ArrowRight, Shield, GitBranch, Landmark, Code2, Store, Lock, ArrowUpRight
} from 'lucide-react';
import { usePageContext } from '../contexts/PageContext';
import wobLogo from '../logos/wob.svg';

/* ─── Scroll-reveal ─── */
const useIntersectionObserver = (options = {}) => {
    const elementRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); }
        }, { threshold: 0.1, ...options });
        if (elementRef.current) observer.observe(elementRef.current);
        return () => observer.disconnect();
    }, [options]);
    return [elementRef, isVisible];
};

const FadeIn = ({ children, delay = 0, className = "" }) => {
    const [ref, isVisible] = useIntersectionObserver();
    return (
        <div
            ref={ref}
            style={{ transitionDelay: `${delay}ms` }}
            className={`transition-all duration-700 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
        >
            {children}
        </div>
    );
};

/* ─── Data-flow Diagram (light palette) ─── */
const DataFlowDiagram = () => (
    <div className="relative w-full max-w-xl mx-auto">
        <svg viewBox="0 0 500 280" className="w-full h-auto" fill="none">
            {/* Connection lines */}
            <path d="M85 70 Q175 70 250 140" stroke="url(#lg1)" strokeWidth="1.5" strokeDasharray="5 3" className="animate-dash" />
            <path d="M85 140 Q175 140 250 140" stroke="url(#lg1)" strokeWidth="1.5" strokeDasharray="5 3" className="animate-dash" style={{ animationDelay: '400ms' }} />
            <path d="M85 210 Q175 210 250 140" stroke="url(#lg1)" strokeWidth="1.5" strokeDasharray="5 3" className="animate-dash" style={{ animationDelay: '800ms' }} />
            <path d="M250 140 Q325 140 415 70" stroke="url(#lg2)" strokeWidth="1.5" strokeDasharray="5 3" className="animate-dash" style={{ animationDelay: '1200ms' }} />
            <path d="M250 140 Q325 140 415 140" stroke="url(#lg2)" strokeWidth="1.5" strokeDasharray="5 3" className="animate-dash" style={{ animationDelay: '1600ms' }} />
            <path d="M250 140 Q325 140 415 210" stroke="url(#lg2)" strokeWidth="1.5" strokeDasharray="5 3" className="animate-dash" style={{ animationDelay: '2000ms' }} />
            <defs>
                <linearGradient id="lg1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#d4d4d8" />
                    <stop offset="100%" stopColor="#18181b" />
                </linearGradient>
                <linearGradient id="lg2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#18181b" />
                    <stop offset="100%" stopColor="#a1a1aa" />
                </linearGradient>
            </defs>

            {/* Left nodes */}
            <rect x="15" y="48" width="130" height="42" rx="8" fill="#fafafa" stroke="#e4e4e7" strokeWidth="1.5" />
            <text x="80" y="74" textAnchor="middle" fill="#71717a" fontSize="12" fontWeight="600" fontFamily="Outfit, sans-serif">ERP / SAP</text>
            <rect x="15" y="118" width="130" height="42" rx="8" fill="#fafafa" stroke="#e4e4e7" strokeWidth="1.5" />
            <text x="80" y="144" textAnchor="middle" fill="#71717a" fontSize="12" fontWeight="600" fontFamily="Outfit, sans-serif">CRM / Salesforce</text>
            <rect x="15" y="188" width="130" height="42" rx="8" fill="#fafafa" stroke="#e4e4e7" strokeWidth="1.5" />
            <text x="80" y="214" textAnchor="middle" fill="#71717a" fontSize="12" fontWeight="600" fontFamily="Outfit, sans-serif">SQL / Data Lake</text>

            {/* Center node */}
            <circle cx="250" cy="140" r="38" fill="#18181b" />
            <circle cx="250" cy="140" r="26" fill="#27272a" />
            <text x="250" y="137" textAnchor="middle" fill="#ffffff" fontSize="11" fontWeight="700" fontFamily="Outfit, sans-serif">MCP</text>
            <text x="250" y="150" textAnchor="middle" fill="#a1a1aa" fontSize="8" fontWeight="500" fontFamily="Outfit, sans-serif">Protocol</text>

            {/* Right nodes */}
            <rect x="370" y="48" width="115" height="42" rx="8" fill="#fafafa" stroke="#e4e4e7" strokeWidth="1.5" />
            <text x="427" y="74" textAnchor="middle" fill="#71717a" fontSize="11" fontWeight="600" fontFamily="Outfit, sans-serif">LLM Agent</text>
            <rect x="370" y="118" width="115" height="42" rx="8" fill="#fafafa" stroke="#e4e4e7" strokeWidth="1.5" />
            <text x="427" y="144" textAnchor="middle" fill="#71717a" fontSize="11" fontWeight="600" fontFamily="Outfit, sans-serif">AI Copilot</text>
            <rect x="370" y="188" width="115" height="42" rx="8" fill="#fafafa" stroke="#e4e4e7" strokeWidth="1.5" />
            <text x="427" y="214" textAnchor="middle" fill="#71717a" fontSize="11" fontWeight="600" fontFamily="Outfit, sans-serif">Smart Search</text>
        </svg>
        <div className="flex justify-between px-6 mt-3 text-[10px] font-semibold uppercase tracking-[0.15em]">
            <span className="text-zinc-400">Legacy Systems</span>
            <span className="text-zinc-900">Panometra MCP</span>
            <span className="text-zinc-400">AI Agents</span>
        </div>
    </div>
);

/* ═══════════════════════════════════════════
   Main Page Component
   ═══════════════════════════════════════════ */
export const AITransformationPage = () => {
    const { navigateTo } = usePageContext();

    const openEmail = (subject = "Enterprise AI Engagement") => {
        const body = "Hi,\n\nI'd like to learn more about your AI Transformation services.\n\nThank you";
        const mailto = `mailto:hello@panometra.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.open(mailto, '_self');
    };

    const scrollToServices = () => {
        const el = document.getElementById('service-tiers');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    /* ─── Service tiers ─── */
    const tiers = [
        { num: 'I', title: 'Strategic Blueprint', desc: 'We analyze your data systems, infrastructure, and high-ROI use cases.'/*, time: '2–3 Weeks', price: 'From €10k', tag: 'DISCOVERY' */ },
        { num: 'II', title: 'Target State Deployment', desc: 'We deploy the first MCP infrastructure layer connecting AI to your critical systems.'/*, time: '4–8 Weeks', price: 'From €25k', tag: 'CORE', featured: true */ },
        { num: 'III', title: 'Enterprise Integration', desc: 'We extend the infrastructure across multiple systems and teams.'/*, time: '10–16 Weeks', price: 'From €70k', tag: 'SCALE' */ },
        { num: 'IV', title: 'Managed Infrastructure', desc: 'We provide ongoing SLA-backed maintenance, protocol updates, and continuous security patching.'/*, time: 'Retainer', price: 'From €4k/mo', tag: 'ONGOING' */ },
    ];

    /* ─── Verticals ─── */
    const verticals = [
        { icon: Landmark, title: 'Financial & Professional Services', hubs: 'EMEA', desc: 'Secure, sovereign-cloud MCP servers for AI-driven legal, compliance, and financial analysis in high-margin, compliance-burdened sectors.' },
        { icon: Code2, title: 'SaaS Platforms', hubs: 'Global', desc: 'Embed "AI Copilot" functionality into your platform without building the complex data-connector protocol layer from scratch.' },
        { icon: Store, title: 'Retail & E-Commerce', hubs: 'Global', desc: 'AI agents that dynamically access real-time inventory, CRM, and supply chain data for customer-facing and internal optimization.' },
    ];

    return (
        <div className="font-sans antialiased bg-white text-zinc-900 selection:bg-zinc-900 selection:text-white overflow-hidden">

            {/* ═══ HEADER ═══ */}
            <nav className="fixed w-full z-40 bg-white/90 backdrop-blur-md border-b border-zinc-100">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-[72px] flex items-center justify-between">
                    <div onClick={() => navigateTo('landing')} className="flex items-center cursor-pointer group">
                        <img src={wobLogo} alt="Panometra" className="h-5 sm:h-7 w-auto group-hover:opacity-70 transition-opacity duration-300 invert" />
                    </div>
                    <a
                        href="mailto:hello@panometra.com?subject=Getting%20Started%20%E2%80%94%20AI%20Transformation&body=Hi%2C%0A%0AI'd%20like%20to%20learn%20more%20about%20your%20AI%20Transformation%20services.%0A%0AThank%20you"
                        className="group inline-flex items-center gap-2 px-5 py-2 bg-zinc-900 text-white text-xs font-semibold uppercase tracking-wider rounded-full hover:bg-zinc-800 transition-all duration-200"
                    >
                        Get Started
                        <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                    </a>
                </div>
            </nav>

            {/* ═══ HERO ═══ */}
            <section className="pt-36 pb-20 lg:pt-44 lg:pb-28">
                <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
                        <div className="space-y-7">
                            <FadeIn delay={50}>
                                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Enterprise AI Infrastructure</p>
                            </FadeIn>
                            <FadeIn delay={100}>
                                <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-[1.1] text-zinc-900">
                                    Turn your company's data into working &nbsp;AI.
                                </h1>
                            </FadeIn>
                            <FadeIn delay={180}>
                                <p className="text-lg sm:text-xl text-zinc-500 leading-relaxed max-w-lg">
                                    Panometra deploys <span className="text-zinc-900 font-medium">Model Context Protocol (MCP)</span> infrastructure that connects AI systems to your ERP, CRM, and data platforms—securely and compliantly.
                                </p>
                            </FadeIn>
                            <FadeIn delay={220}>
                                <p className="text-sm text-zinc-400 max-w-md leading-relaxed">
                                    Strictly adhering to EMEA data sovereignty mandates. Built for GDPR and the EU AI Act from the ground up.
                                </p>
                            </FadeIn>
                            <FadeIn delay={280}>
                                <div className="flex flex-wrap gap-3 pt-2">
                                    <a
                                        href="mailto:hello@panometra.com?subject=MCP%20Infrastructure%20Deployment&body=Hi%2C%0A%0AI'd%20like%20to%20learn%20more%20about%20your%20AI%20Transformation%20services.%0A%0AThank%20you"
                                        className="group inline-flex items-center gap-2 px-7 py-3.5 bg-zinc-900 text-white rounded-full font-semibold text-sm hover:bg-zinc-800 transition-all duration-200"
                                    >
                                        Contact Sales
                                        <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                                    </a>
                                    <button
                                        onClick={scrollToServices}
                                        className="px-7 py-3.5 border border-zinc-200 text-zinc-600 rounded-full font-medium text-sm hover:border-zinc-400 hover:text-zinc-900 transition-all duration-200"
                                    >
                                        Read Whitepaper
                                    </button>
                                </div>
                            </FadeIn>
                        </div>

                        <FadeIn delay={350} className="hidden lg:block">
                            <div className="relative p-8 rounded-2xl border border-zinc-100 bg-white shadow-sm">
                                <DataFlowDiagram />
                                <div className="absolute -top-3 -right-3 px-3 py-1.5 bg-zinc-900 rounded-full flex items-center gap-1.5 shadow-md">
                                    <Lock size={12} className="text-zinc-400" />
                                    <span className="text-[10px] font-semibold text-white tracking-wide uppercase">GDPR Compliant</span>
                                </div>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* ═══ DIVIDER ═══ */}
            <div className="max-w-[1200px] mx-auto px-5 sm:px-8"><div className="border-t border-zinc-100" /></div>

            {/* ═══ THE ENTERPRISE BOTTLENECK ═══ */}
            <section className="py-24 lg:py-28">
                <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
                    <div className="max-w-2xl mx-auto text-center mb-16">
                        <FadeIn>
                            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-4">The Enterprise Bottleneck</p>
                            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5 leading-tight">
                                You invest in AI. But the AI cannot securely access the systems that matter.
                            </h2>
                            <p className="text-base text-zinc-500 leading-relaxed">
                                Proprietary data resides in highly guarded, fragmented systems. Equipping AI agents to securely query and reason over these systems requires a niche intersection of protocol engineering and enterprise security.
                            </p>
                        </FadeIn>
                    </div>

                    {/* Mobile diagram */}
                    <FadeIn className="lg:hidden mb-14">
                        <div className="p-5 rounded-xl border border-zinc-100 bg-zinc-50/50 overflow-x-auto">
                            <DataFlowDiagram />
                        </div>
                    </FadeIn>

                    <div className="grid sm:grid-cols-2 gap-6">
                        <FadeIn delay={80}>
                            <div className="group p-8 rounded-2xl border border-zinc-100 bg-white hover:shadow-lg hover:shadow-zinc-100/80 transition-all duration-300 h-full">
                                <div className="w-11 h-11 rounded-xl bg-zinc-100 flex items-center justify-center mb-5 group-hover:bg-zinc-900 transition-colors duration-300">
                                    <Shield size={20} className="text-zinc-500 group-hover:text-white transition-colors duration-300" />
                                </div>
                                <h3 className="text-lg font-bold mb-2">Regulatory-First Architecture</h3>
                                <p className="text-sm text-zinc-500 leading-relaxed">
                                    Built from the ground up for GDPR and the incoming EU AI Act. Privacy-by-design with on-premise or sovereign cloud routing.
                                </p>
                            </div>
                        </FadeIn>
                        <FadeIn delay={160}>
                            <div className="group p-8 rounded-2xl border border-zinc-100 bg-white hover:shadow-lg hover:shadow-zinc-100/80 transition-all duration-300 h-full">
                                <div className="w-11 h-11 rounded-xl bg-zinc-100 flex items-center justify-center mb-5 group-hover:bg-zinc-900 transition-colors duration-300">
                                    <GitBranch size={20} className="text-zinc-500 group-hover:text-white transition-colors duration-300" />
                                </div>
                                <h3 className="text-lg font-bold mb-2">Protocol Sovereignty</h3>
                                <p className="text-sm text-zinc-500 leading-relaxed">
                                    Anthropic's open-source MCP is the new standard. We provide the specialized engineering talent to implement it before your competitors.
                                </p>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* ═══ DIVIDER ═══ */}
            <div className="max-w-[1200px] mx-auto px-5 sm:px-8"><div className="border-t border-zinc-100" /></div>

            {/* ═══ SERVICE ARCHITECTURE ═══ */}
            <section id="service-tiers" className="py-24 lg:py-28">
                <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
                    <div className="max-w-2xl mx-auto text-center mb-16">
                        <FadeIn>
                            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-4">Service Architecture</p>
                            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5 leading-tight">
                                From strategic alignment to enterprise-wide deployment.
                            </h2>
                            <p className="text-base text-zinc-500">
                                Shepherding clients from initial discovery through technical deployment and ongoing governance.
                            </p>
                        </FadeIn>
                    </div>

                    <div className="space-y-4">
                        {tiers.map((t, i) => (
                            <FadeIn key={i} delay={i * 60}>
                                <div className={`group relative p-6 sm:p-8 rounded-2xl border transition-all duration-300 ${t.featured
                                    ? 'border-zinc-900 bg-zinc-900 text-white shadow-lg'
                                    : 'border-zinc-100 bg-white hover:shadow-md hover:shadow-zinc-100/80'
                                    }`}>
                                    {t.featured && (
                                        <div className="absolute -top-2.5 left-6 px-3 py-0.5 bg-white text-zinc-900 rounded-full text-[10px] font-bold uppercase tracking-widest">
                                            Core Deployment
                                        </div>
                                    )}
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
                                        <div className={`text-3xl font-bold w-12 flex-shrink-0 ${t.featured ? 'text-white' : 'text-zinc-200'}`}>
                                            {t.num}
                                        </div>
                                        <div className="flex-grow min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                                <h4 className="text-base font-bold">{t.title}</h4>
                                                {!t.featured && (
                                                    <span className="px-2 py-0.5 rounded-full bg-zinc-50 border border-zinc-100 text-zinc-400 text-[10px] font-semibold uppercase tracking-wider">{t.tag}</span>
                                                )}
                                            </div>
                                            <p className={`text-sm leading-relaxed ${t.featured ? 'text-zinc-400' : 'text-zinc-500'}`}>{t.desc}</p>
                                        </div>
                                        <div className="flex-shrink-0 flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0.5 sm:text-right">
                                            <span className={`text-sm font-bold ${t.featured ? 'text-white' : 'text-zinc-900'}`}>{t.price}</span>
                                            <span className={`text-xs ${t.featured ? 'text-zinc-500' : 'text-zinc-400'}`}>{t.time}</span>
                                        </div>
                                    </div>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══ DIVIDER ═══ */}
            <div className="max-w-[1200px] mx-auto px-5 sm:px-8"><div className="border-t border-zinc-100" /></div>

            {/* ═══ VERTICALS ═══ */}
            <section className="py-24 lg:py-28">
                <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
                    <div className="max-w-2xl mx-auto text-center mb-16">
                        <FadeIn>
                            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-4">Engineered for EMEA Leaders</p>
                            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5 leading-tight">
                                Mid-market to enterprise. Dense data architectures.
                            </h2>
                            <p className="text-base text-zinc-500">
                                Panometra works with organizations where data complexity blocks AI adoption.
                            </p>
                        </FadeIn>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-6">
                        {verticals.map((v, i) => (
                            <FadeIn key={i} delay={i * 100}>
                                <div className="group p-8 rounded-2xl border border-zinc-100 bg-white hover:shadow-lg hover:shadow-zinc-100/80 transition-all duration-300 h-full flex flex-col">
                                    <div className="w-11 h-11 rounded-xl bg-zinc-100 flex items-center justify-center mb-5 group-hover:bg-zinc-900 transition-colors duration-300">
                                        <v.icon size={20} className="text-zinc-500 group-hover:text-white transition-colors duration-300" />
                                    </div>
                                    <h4 className="text-base font-bold mb-1">{v.title}</h4>
                                    <p className="text-[11px] font-semibold text-zinc-400 mb-3 tracking-wide uppercase">
                                        {v.hubs}
                                    </p>
                                    <p className="text-sm text-zinc-500 leading-relaxed flex-grow">{v.desc}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══ CTA ═══ */}
            <section className="py-24 lg:py-28 bg-zinc-50">
                <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
                    <FadeIn>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5">
                            Ready to activate your enterprise&nbsp;data?
                        </h2>
                        <p className="text-base text-zinc-500 mb-10 max-w-lg mx-auto leading-relaxed">
                            Transition from IT cost-center to executive value-creation. Schedule a strategic diagnostic with our protocol engineers.
                        </p>
                        <a
                            href="mailto:hello@panometra.com?subject=Strategic%20Diagnostic%20%E2%80%94%20AI%20Transformation&body=Hi%2C%0A%0AI'd%20like%20to%20schedule%20a%20strategic%20diagnostic%20regarding%20AI%20Transformation.%0A%0AThank%20you"
                            className="group inline-flex items-center gap-2 px-10 py-4 bg-zinc-900 text-white rounded-full font-semibold text-sm hover:bg-zinc-800 transition-all duration-200 shadow-lg shadow-zinc-900/10"
                        >
                            Initiate Engagement
                            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                        </a>
                    </FadeIn>
                </div>
            </section>

            {/* ─── Inline animations ─── */}
            <style>{`
        @keyframes dash { to { stroke-dashoffset: -20; } }
        .animate-dash { animation: dash 2s linear infinite; }
      `}</style>
        </div>
    );
};
